import { runInAction } from "mobx";

import { KeyTextValue } from "@libs/api/ref-data/RefDataAccessor.ts";
import {
  ClaimAdjustmentRequestType,
  ClaimAdjustmentResponseRequest,
  ClaimDiagnosisDto,
  DiagnosisSides
} from "@libs/gateways/acc/AccGateway.dtos.ts";
import { EpisodeOfCareDto } from "@libs/gateways/clinical/ClinicalGateway.dtos.ts";
import { ProviderAccContractTypeDto } from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { DischargeStatus } from "@shared-types/clinical/discharge-status.enum.ts";
import { ClinicalStore } from "@stores/clinical/ClinicalStore.ts";
import { ClinicalRecord } from "@stores/clinical/models/ClinicalRecord.ts";
import { RootStore } from "@stores/root/RootStore.ts";

import { AccStore } from "../AccStore.ts";

export const isTreatmentsRequest = (request: ClaimAdjustmentResponseRequest) =>
  request.claimAdjustmentRequestTypeCode ===
  ClaimAdjustmentRequestType.TreatmentRequest;

export const isDiagnosisAdditionRequest = (
  request: ClaimAdjustmentResponseRequest
) =>
  request.claimAdjustmentRequestTypeCode ===
  ClaimAdjustmentRequestType.DiagnosisAddition;

export const isDiagnosisChangeRequest = (
  request: ClaimAdjustmentResponseRequest
) =>
  request.claimAdjustmentRequestTypeCode ===
  ClaimAdjustmentRequestType.DiagnosisChange;

export const isSideChangeRequest = (request: ClaimAdjustmentResponseRequest) =>
  request.claimAdjustmentRequestTypeCode ===
  ClaimAdjustmentRequestType.SideChange;
export const filterAvailableAccProviderTypes = (
  accProviderTypes: KeyTextValue[],
  providerContractTypes: ProviderAccContractTypeDto[],
  contractTypes?: string[]
): KeyTextValue[] => {
  const availableProviderCC = providerContractTypes.filter(
    pcc => contractTypes?.some(contractType => pcc.code === contractType)
  );
  return accProviderTypes.filter(({ key }) =>
    availableProviderCC.some(acc => acc.providerTypeCode === key)
  );
};

// unlinkEpisodeOfCareFromEncounter and updateEncounterEpisodeOfCareId were originally from ConditionSidePanelHelper
//  but they have been moved here so it can be used elsewhere in the app.

export const unlinkEpisodeOfCareFromEncounter = async (
  clinicalRecord: ClinicalRecord,
  root: RootStore
) => {
  if (!clinicalRecord.openEncounter?.id) return;

  const existingDischarge =
    clinicalRecord.currentConditionDischargeDataItems?.some(dataItem => {
      return (
        dataItem?.dischargeStatus === DischargeStatus.Reversed ||
        dataItem?.dischargeStatus === DischargeStatus.Completed
      );
    });

  //when unlink need if original episodeofcare is discharged then update episodeofcare to discharged
  if (existingDischarge && clinicalRecord?.episodeOfCare) {
    await updateEpisodeOfCareDischargeStatus(
      clinicalRecord?.episodeOfCare?.dto,
      true,
      root.clinical
    );
  }
  // need to remove the diagnoses before we unlink the condition
  await clinicalRecord.removeConditionDiagnosisFromReasonForVisit();

  if (!!clinicalRecord.episodeOfCare?.claim?.id) {
    await root.acc.deleteClaimEncounter(
      clinicalRecord.episodeOfCare.claim.id,
      clinicalRecord.openEncounter.id
    );
  }
  // these two api calls need to finish before we reload everything
  await Promise.all([
    updateEncounterEpisodeOfCareId(undefined, clinicalRecord, root.clinical),
    root.clinical.discardEpisodeClinicalData(clinicalRecord.openEncounter.id)
  ]);

  await Promise.all([
    clinicalRecord.loadClinicalData(),
    clinicalRecord.loadPatientEpisodesOfCare(),
    clinicalRecord.loadLinkedEncounters(),
    clinicalRecord.loadStructuredNotes()
  ]);

  clinicalRecord.stashedClinicalData?.resetStashedClinicalData([
    "injury",
    "physioBody",
    "patientTreatmentPlan"
  ]);
};

export const updateEpisodeOfCareDischargeStatus = async (
  episodeOfCare: EpisodeOfCareDto,
  discharged: boolean,
  clinical: ClinicalStore
) => {
  const updatedEpisodeOfCare = {
    ...episodeOfCare,
    discharged
  };

  await clinical.updateEpisodeOfCare(updatedEpisodeOfCare, episodeOfCare.id);
};

export const updateEncounterEpisodeOfCareId = async (
  episodeOfCareId: string | undefined,
  clinicalRecord: ClinicalRecord,
  clinical: ClinicalStore
) => {
  if (!clinicalRecord.openEncounter) return;

  const episodeOfCare = episodeOfCareId
    ? await clinical.getEpisodeOfCare(episodeOfCareId)
    : undefined;

  // Update the EpisodeOfCare ID of the current encounter
  await clinical.updateEncounter({
    id: clinicalRecord.openEncounter.id,
    episodeOfCareId: episodeOfCareId || null,
    eTag: clinicalRecord.openEncounter.eTag
  });

  runInAction(() => {
    clinicalRecord.episodeOfCare = episodeOfCare;
  });
};

export const getDiagnosisText = (
  diagnosis: ClaimDiagnosisDto,
  acc: AccStore
) => {
  const text = diagnosis.terminology?.text;
  if (!text) return text;

  if (
    !diagnosis.diagnosisSide ||
    diagnosis.diagnosisSide === DiagnosisSides.NotApplicable
  ) {
    return text;
  }

  const side = diagnosis.diagnosisSide
    ? acc.ref.diagnosisSides.get(diagnosis.diagnosisSide)
    : undefined;

  if (!side) return text;

  return `${text} (${side.text})`;
};
